import {
  compareHuge,
  huge,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";

// Current BHG -> HG power exponent base; see ADR 0025 for layer intent.
// Base 1 keeps BHG unchanged.
export const BHG_TO_HG_POWER_EXPONENT_BASE = 1;

export interface HovGainPipelineInput {
  // Additive sources before skill gain and milestone scaling.
  readonly raw: HovGainPipelineRawInput;
  // Base HG source after skill gain and the global HG milestone multiplier.
  readonly base: HovGainPipelineBaseInput;
  // Inner Hov-side multipliers that form BHG before the BHG -> HG transform.
  readonly bhg: HovGainPipelineBhgInput;
  // BHG -> HG transform layer. This is before Amplifier / AP.
  readonly hg: HovGainPipelineHgInput;
  // Outer effective-HG layer for Amplifier / AP and future outer effects.
  readonly ehg: HovGainPipelineEhgInput;
}

export interface HovGainPipelineRawInput {
  readonly baseHovGainPerSecond: HugeNumberInput;
  readonly upgradeGainIncreasePerPurchase: HugeNumberInput;
  readonly milestoneAdjustedPurchaseCount: number;
  readonly hiddenHovGainBonus: HugeNumberInput;
  readonly achievementFlatHovGainBonus: HugeNumberInput;
}

export interface HovGainPipelineBaseInput {
  readonly skillHovGainPerSecond: HugeNumberInput;
  readonly upgradeMilestoneMultiplier: HugeNumberInput;
}

export interface HovGainPipelineBhgInput {
  readonly entitlementHovIncomeMultiplier: HugeNumberInput;
  readonly mHovHgMultiplier: HugeNumberInput;
  readonly hovGainCountMultiplier: HugeNumberInput;
  readonly hovGainMultiplier: HugeNumberInput;
}

export interface HovGainPipelineHgInput {
  readonly bhgToHgPowerExponent: number;
  // Optional post-exponent HG weakening applied while a constraining Wall (HG
  // Wall) is active: `effectiveHg = (hg / divisor) ** exponent`, evaluated in
  // HugeNumber space. Omitted (and therefore neutral) outside that wall.
  readonly activeConstraint?: HovGainPipelineHgActiveConstraint;
}

export interface HovGainPipelineHgActiveConstraint {
  readonly divisor: number;
  readonly exponent: number;
}

export interface HovGainPipelineEhgInput {
  readonly amplifierHgMultiplier: HugeNumberInput;
  // Persistent reward multiplier from cleared Walls. Defaults to 1 when no
  // Wall reward applies.
  readonly wallRewardMultiplier: HugeNumberInput;
}

export interface HovGainPipelineResult {
  readonly rawHovGainPerSecond: HugeNumber;
  readonly baseHovGainPerSecond: HugeNumber;
  readonly bhgHovGainPerSecond: HugeNumber;
  readonly hgHovGainPerSecond: HugeNumber;
  readonly amplifiedHovGainPerSecond: HugeNumber;
  readonly finalHovGainPerSecond: HugeNumber;
}

export function resolveHovGainPipeline(
  input: HovGainPipelineInput,
): HovGainPipelineResult {
  const rawHovGainPerSecond = huge(input.raw.baseHovGainPerSecond)
    .add(
      huge(input.raw.upgradeGainIncreasePerPurchase).mul(
        input.raw.milestoneAdjustedPurchaseCount,
      ),
    )
    .add(input.raw.hiddenHovGainBonus)
    .add(input.raw.achievementFlatHovGainBonus);

  const baseHovGainPerSecond = rawHovGainPerSecond
    .add(input.base.skillHovGainPerSecond)
    .mul(input.base.upgradeMilestoneMultiplier);

  const bhgHovGainPerSecond = baseHovGainPerSecond
    .mul(input.bhg.entitlementHovIncomeMultiplier)
    .mul(input.bhg.mHovHgMultiplier)
    .mul(input.bhg.hovGainCountMultiplier)
    .mul(input.bhg.hovGainMultiplier);

  const hgHovGainPerSecond = applyActiveHgConstraint(
    applyBhgToHgPowerExponent(
      bhgHovGainPerSecond,
      input.hg.bhgToHgPowerExponent,
    ),
    input.hg.activeConstraint,
  );

  const amplifiedHovGainPerSecond = hgHovGainPerSecond
    .mul(input.ehg.amplifierHgMultiplier)
    .mul(input.ehg.wallRewardMultiplier);

  return {
    rawHovGainPerSecond,
    baseHovGainPerSecond,
    bhgHovGainPerSecond,
    hgHovGainPerSecond,
    amplifiedHovGainPerSecond,
    finalHovGainPerSecond: amplifiedHovGainPerSecond,
  };
}

function applyBhgToHgPowerExponent(
  bhgHovGainPerSecond: HugeNumber,
  exponent: number,
): HugeNumber {
  if (exponent === BHG_TO_HG_POWER_EXPONENT_BASE) return bhgHovGainPerSecond;
  if (!Number.isFinite(exponent) || exponent < 0) return bhgHovGainPerSecond;
  if (!bhgHovGainPerSecond.isFinite()) return bhgHovGainPerSecond;
  if (compareHuge(bhgHovGainPerSecond, 0) <= 0) return bhgHovGainPerSecond;
  return bhgHovGainPerSecond.pow(exponent);
}

function applyActiveHgConstraint(
  hgHovGainPerSecond: HugeNumber,
  constraint: HovGainPipelineHgActiveConstraint | undefined,
): HugeNumber {
  if (constraint === undefined) return hgHovGainPerSecond;
  const { divisor, exponent } = constraint;
  if (!Number.isFinite(divisor) || divisor <= 0) return hgHovGainPerSecond;
  if (!Number.isFinite(exponent) || exponent < 0) return hgHovGainPerSecond;
  if (!hgHovGainPerSecond.isFinite()) return hgHovGainPerSecond;
  if (compareHuge(hgHovGainPerSecond, 0) <= 0) return hgHovGainPerSecond;
  return hgHovGainPerSecond.div(divisor).pow(exponent);
}
